import React from "react";
import { Box } from "@mui/material";
import { motion } from "framer-motion";
import PropTypes from "prop-types";

const GRAIN_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.05'/%3E%3C/svg%3E")`;

// Idle resting angles (degrees) when no cursor focus / before first move.
const IDLE = { tl: 35, tr: -35, bl: 35, br: -35 };

const VolumetricLayout = ({ children, enableLights = false, variant = "warm", disablePadding = false }) => {
  // === ARCHITECTURE: why this is transform-only ===
  // The old design animated each beam's `height` via React state on every
  // mousemove. `height` is a LAYOUT property — animating it on a
  // `filter: blur(80px)` + `mixBlendMode: screen` surface invalidates the
  // blurred raster cache every frame, forcing the GPU to re-blur a huge
  // screen-blended layer over whatever cards sit behind it. That re-raster is
  // the real cost (the lag + flicker over cards), not the event frequency.
  //
  // Fix: beams now have a FIXED height and only ROTATE. Rotation is a
  // compositor-only transform (no raster invalidation). The angles are pushed
  // through CSS custom properties set imperatively on the container ref inside
  // a rAF — so the cursor never triggers a React re-render. Opacity is static
  // (the old idle opacity keyframes were also re-rasterising the blur).
  const containerRef = React.useRef(null);
  const rafRef = React.useRef(null);
  const pendingRef = React.useRef(null);

  // Compute the 4 beam angles (degrees) pointing from each corner toward cursor.
  const computeAngles = (targetX, targetY, width, height) => {
    const ORIGIN_Y = -150;
    const ORIGIN_BOTTOM_Y = height + 150;
    const ORIGIN_LEFT_X = -50;
    const ORIGIN_RIGHT_X = width + 50;

    const dxLeft = targetX - ORIGIN_LEFT_X;
    const dxRight = targetX - ORIGIN_RIGHT_X;
    const dyTop = targetY - ORIGIN_Y;
    const dyBottom = targetY - ORIGIN_BOTTOM_Y;

    return {
      tl: -1 * ((Math.atan2(dxLeft, dyTop) * 180) / Math.PI),
      tr: -1 * ((Math.atan2(dxRight, dyTop) * 180) / Math.PI),
      bl: (Math.atan2(dxLeft, -dyBottom) * 180) / Math.PI,
      br: (Math.atan2(dxRight, -dyBottom) * 180) / Math.PI,
    };
  };

  const applyAngles = (a) => {
    const el = containerRef.current;
    if (!el) return;
    el.style.setProperty("--beam-tl", `${a.tl}deg`);
    el.style.setProperty("--beam-tr", `${a.tr}deg`);
    el.style.setProperty("--beam-bl", `${a.bl}deg`);
    el.style.setProperty("--beam-br", `${a.br}deg`);
  };

  // Seed idle angles once so beams have a defined transform before first move.
  React.useEffect(() => {
    if (!enableLights) return;
    applyAngles(IDLE);
  }, [enableLights]);

  React.useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  const handleMouseMove = (e) => {
    if (!enableLights) return;
    const rect = e.currentTarget.getBoundingClientRect();
    pendingRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      w: rect.width,
      h: rect.height,
    };
    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const p = pendingRef.current;
        if (p) applyAngles(computeAngles(p.x, p.y, p.w, p.h));
      });
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        minHeight: "100vh",
        position: "relative",
        background: "transparent", // Let GhostCursor show through
        overflow: "hidden",
        zIndex: 1,
      }}
      onMouseMove={handleMouseMove}
    >
      {/* === VOLUMETRIC STAGE LIGHTING ENGINE === */}
      
      {/* 1. ATMOSPHERIC BASE (The Sanctuary Void) */}
      <Box 
        sx={{
          position: "absolute",
          inset: 0,
          background: variant === "deep-cosmos" ? "#040406" : "#0A0908", // Deep Cosmos vs Warm Obsidian
          opacity: 1,
          pointerEvents: "none",
          zIndex: 0,
        }} 
      />
      
      {/* === BACKGROUND VARIANTS === */}
      {/* 2. THE DIVINE LIGHT (Subtle Top Gradient) */}
      {/* 2. THE DIVINE LIGHT (Subtle Top Gradient) */}
      {(variant === "warm" || variant === "platinum" || variant === "deep-cosmos") && (
      <Box 
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "80vh",
          background: 
            variant === "warm" ? "radial-gradient(ellipse at 50% -20%, rgba(255, 241, 230, 0.08) 0%, transparent 70%)" :
            variant === "platinum" ? "radial-gradient(ellipse at 50% -20%, rgba(226, 232, 240, 0.12) 0%, transparent 70%)" :
            "radial-gradient(ellipse at 50% -20%, rgba(99, 102, 241, 0.15) 0%, transparent 80%)", // Deep Cosmos (Indigo)
          opacity: 1,
          pointerEvents: "none",
          zIndex: 0,
          mixBlendMode: "screen"
        }} 
      />
      )}

      {/* 3. PRISM LIGHTS (Stained Glass Effect) - Deep Rich Colors */}
      {variant !== "void" && (
        <>
      {/* LEFT PRISM */}
      <Box 
        component={variant === "deep-cosmos" ? motion.div : "div"}
        animate={variant === "deep-cosmos" ? { opacity: [0.6, 0.8, 0.6], scale: [1, 1.1, 1] } : {}}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        sx={{
          position: "absolute",
          top: "10%",
          left: "5%",
          width: "40vw",
          height: "40vw",
          background: 
             variant === "warm" ? "radial-gradient(circle, rgba(88, 28, 135, 0.15) 0%, transparent 70%)" :
             variant === "platinum" ? "radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)" :
             "radial-gradient(circle, rgba(79, 70, 229, 0.12) 0%, transparent 70%)", // Deep Cosmos (Violet)
          filter: "blur(80px)",
          opacity: 0.8,
          pointerEvents: "none",
          zIndex: 0,
          mixBlendMode: "screen"
        }} 
      />
      
      {/* RIGHT PRISM */}
      <Box 
        component={variant === "deep-cosmos" ? motion.div : "div"}
        animate={variant === "deep-cosmos" ? { opacity: [0.5, 0.7, 0.5], scale: [1.1, 1, 1.1] } : {}}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        sx={{
          position: "absolute",
          top: "20%",
          right: "5%",
          width: "35vw",
          height: "35vw",
          background: 
             variant === "warm" ? "radial-gradient(circle, rgba(124, 45, 18, 0.12) 0%, transparent 70%)" :
             variant === "platinum" ? "radial-gradient(circle, rgba(148, 163, 184, 0.1) 0%, transparent 70%)" :
             "radial-gradient(circle, rgba(14, 165, 233, 0.1) 0%, transparent 70%)", // Deep Cosmos (Cyan)
          filter: "blur(80px)",
          opacity: 0.8,
          pointerEvents: "none",
          zIndex: 0,
          mixBlendMode: "screen"
        }} 
      />
      
      {/* BOTTOM GLOW */}
      <Box 
        component={variant === "deep-cosmos" ? motion.div : "div"}
        animate={variant === "deep-cosmos" ? { opacity: [0.4, 0.6, 0.4] } : {}}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        sx={{
          position: "absolute",
          bottom: "10%",
          left: "20%",
          width: "50vw",
          height: "30vh",
          background: "radial-gradient(ellipse, rgba(30, 58, 138, 0.1) 0%, transparent 70%)", // Royal Blue (Shared)
          filter: "blur(100px)",
          opacity: 0.6,
          pointerEvents: "none",
          zIndex: 0,
          mixBlendMode: "screen"
        }} 
      />
      </>
      )}

      {enableLights && (
        <>
      {/* 2. LEFT BEAM CLUSTER — transform-only (rotate via --beam-tl) */}
      {/* Ray 1: SMOKE GLOW (Atmosphere) */}
      <Box
        sx={{
          position: "absolute",
          top: "-150px",
          left: "calc(-50px - 17.5vw)", // Offset by half of new width (35vw / 2)
          width: "35vw", // Wider base for more dispersion
          height: "150vh", // FIXED — never animated (no blur re-raster)
          transformOrigin: "top center",
          transform: "rotate(var(--beam-tl, 35deg))",
          transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
          willChange: "transform",
          opacity: 0.3,
          filter: "blur(80px)", // Much higher blur for dispersed look
          zIndex: 1,
          pointerEvents: "none",
          mixBlendMode: "screen", // Additive light blending
        }}
      >
        {/* Inner Shape */}
        <Box sx={{
          width: "100%",
          height: "100%",
          background: "linear-gradient(to bottom, rgba(147, 197, 253, 0.35) 0%, rgba(99, 102, 241, 0.03) 95%, transparent 100%)",
          // TRAPEZOID: Narrower top percentage because width is larger
          clipPath: "polygon(42% 0%, 58% 0%, 100% 100%, 0% 100%)",
        }} />

        {/* ATTACHED FLOOR SPOT (Left) - counter-rotates to stay flat */}
        <Box
            sx={{
                position: "absolute",
                bottom: "-90px", // Centered on tip (180px height / 2)
                left: "50%",
                marginLeft: "-300px", // Center 600px width
                width: "600px", // Wider floor spot
                height: "180px", // Taller floor spot
                transform: "rotate(calc(-1 * var(--beam-tl, 35deg)))",
                transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
                willChange: "transform",
                background: "radial-gradient(ellipse at center, color-mix(in srgb, var(--text-primary) 30%, transparent) 0%, rgba(165, 180, 253, 0.15) 40%, transparent 80%)",
                mixBlendMode: "screen",
            }}
        />
      </Box>

      {/* Ray 2: CORE GLOW */}
      <Box
        sx={{
          position: "absolute",
          top: "-150px",
          left: "calc(-50px - 17.5vw)",
          width: "35vw",
          height: "150vh",
          transformOrigin: "top center",
          transform: "rotate(var(--beam-tl, 35deg))",
          transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
          willChange: "transform",
          opacity: 0.25,
          filter: "blur(50px)", // Higher blur
          zIndex: 1,
          pointerEvents: "none",
          mixBlendMode: "screen", // Additive light blending
        }}
      >
         <Box sx={{
          width: "100%",
          height: "100%",
          background: "linear-gradient(to bottom, rgba(165, 180, 253, 0.45) 0%, rgba(129, 140, 248, 0.1) 90%, transparent 100%)",
          clipPath: "polygon(45% 0%, 55% 0%, 80% 100%, 20% 100%)",
        }} />
      </Box>
      
      {/* 3. RIGHT BEAM CLUSTER — transform-only (rotate via --beam-tr) */}
      {/* Ray 1: SMOKE GLOW */}
      <Box
        sx={{
          position: "absolute",
          top: "-150px",
          right: "calc(-50px - 17.5vw)", // Offset by half of new width (35vw / 2)
          width: "35vw", // Wider base for more dispersion
          height: "150vh",
          transformOrigin: "top center",
          transform: "rotate(var(--beam-tr, -35deg))",
          transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
          willChange: "transform",
          opacity: 0.3,
          filter: "blur(80px)", // Much higher blur for dispersed look
          zIndex: 1,
          pointerEvents: "none",
          mixBlendMode: "screen", // Additive light blending
        }}
      >
        <Box sx={{
          width: "100%",
          height: "100%",
          background: "linear-gradient(to bottom, rgba(165, 180, 252, 0.35) 0%, rgba(99, 102, 241, 0.03) 95%, transparent 100%)",
          clipPath: "polygon(42% 0%, 58% 0%, 100% 100%, 0% 100%)",
        }} />

        {/* ATTACHED FLOOR SPOT (Right) - counter-rotates to stay flat */}
        <Box
            sx={{
                position: "absolute",
                bottom: "-90px",
                left: "50%",
                marginLeft: "-300px",
                width: "600px", // Wider floor spot
                height: "180px", // Taller floor spot
                transform: "rotate(calc(-1 * var(--beam-tr, -35deg)))",
                transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
                willChange: "transform",
                background: "radial-gradient(ellipse at center, color-mix(in srgb, var(--text-primary) 30%, transparent) 0%, rgba(165, 180, 253, 0.15) 40%, transparent 80%)",
                mixBlendMode: "screen",
            }}
        />
      </Box>

      {/* Ray 2: CORE GLOW */}
      <Box
        sx={{
          position: "absolute",
          top: "-150px",
          right: "calc(-50px - 17.5vw)",
          width: "35vw",
          height: "150vh",
          transformOrigin: "top center",
          transform: "rotate(var(--beam-tr, -35deg))",
          transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
          willChange: "transform",
          opacity: 0.25,
          filter: "blur(50px)", // Higher blur
          zIndex: 1,
          pointerEvents: "none",
          mixBlendMode: "screen", // Additive light blending
        }}
      >
        <Box sx={{
          width: "100%",
          height: "100%",
          background: "linear-gradient(to bottom, rgba(165, 180, 253, 0.45) 0%, rgba(129, 140, 248, 0.1) 90%, transparent 100%)",
          clipPath: "polygon(45% 0%, 55% 0%, 80% 100%, 20% 100%)",
        }} />
      </Box>

      {/* 4. BOTTOM-LEFT BEAM CLUSTER — transform-only (rotate via --beam-bl) */}
      {/* Ray 1: SMOKE GLOW (Atmosphere) */}
      <Box
        sx={{
          position: "absolute",
          bottom: "-150px",
          left: "calc(-50px - 17.5vw)", // Offset by half of width (35vw / 2)
          width: "35vw",
          height: "150vh",
          transformOrigin: "bottom center",
          transform: "rotate(var(--beam-bl, 35deg))",
          transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
          willChange: "transform",
          opacity: 0.3,
          filter: "blur(80px)", // Dispersed blur
          zIndex: 1,
          pointerEvents: "none",
          mixBlendMode: "screen",
        }}
      >
        {/* Inner Shape (pointing UP) */}
        <Box sx={{
          width: "100%",
          height: "100%",
          background: "linear-gradient(to top, rgba(147, 197, 253, 0.35) 0%, rgba(99, 102, 241, 0.03) 95%, transparent 100%)",
          clipPath: "polygon(0% 0%, 100% 0%, 58% 100%, 42% 100%)",
        }} />

        {/* ATTACHED CEILING SPOT (Bottom-Left) - counter-rotates to stay flat */}
        <Box
            sx={{
                position: "absolute",
                top: "-90px", // Centered at the tip
                left: "50%",
                marginLeft: "-300px", // Center 600px width
                width: "600px",
                height: "180px",
                transform: "rotate(calc(-1 * var(--beam-bl, 35deg)))",
                transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
                willChange: "transform",
                background: "radial-gradient(ellipse at center, color-mix(in srgb, var(--text-primary) 30%, transparent) 0%, rgba(165, 180, 253, 0.15) 40%, transparent 80%)",
                mixBlendMode: "screen",
            }}
        />
      </Box>

      {/* Ray 2: CORE GLOW */}
      <Box
        sx={{
          position: "absolute",
          bottom: "-150px",
          left: "calc(-50px - 17.5vw)",
          width: "35vw",
          height: "150vh",
          transformOrigin: "bottom center",
          transform: "rotate(var(--beam-bl, 35deg))",
          transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
          willChange: "transform",
          opacity: 0.25,
          filter: "blur(50px)",
          zIndex: 1,
          pointerEvents: "none",
          mixBlendMode: "screen",
        }}
      >
        <Box sx={{
          width: "100%",
          height: "100%",
          background: "linear-gradient(to top, rgba(165, 180, 253, 0.45) 0%, rgba(129, 140, 248, 0.1) 90%, transparent 100%)",
          clipPath: "polygon(20% 0%, 80% 0%, 55% 100%, 45% 100%)",
        }} />
      </Box>

      {/* 5. BOTTOM-RIGHT BEAM CLUSTER — transform-only (rotate via --beam-br) */}
      {/* Ray 1: SMOKE GLOW (Atmosphere) */}
      <Box
        sx={{
          position: "absolute",
          bottom: "-150px",
          right: "calc(-50px - 17.5vw)",
          width: "35vw",
          height: "150vh",
          transformOrigin: "bottom center",
          transform: "rotate(var(--beam-br, -35deg))",
          transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
          willChange: "transform",
          opacity: 0.3,
          filter: "blur(80px)",
          zIndex: 1,
          pointerEvents: "none",
          mixBlendMode: "screen",
        }}
      >
        <Box sx={{
          width: "100%",
          height: "100%",
          background: "linear-gradient(to top, rgba(165, 180, 252, 0.35) 0%, rgba(99, 102, 241, 0.03) 95%, transparent 100%)",
          clipPath: "polygon(0% 0%, 100% 0%, 58% 100%, 42% 100%)",
        }} />

        {/* ATTACHED CEILING SPOT (Bottom-Right) - counter-rotates to stay flat */}
        <Box
            sx={{
                position: "absolute",
                top: "-90px",
                left: "50%",
                marginLeft: "-300px",
                width: "600px",
                height: "180px",
                transform: "rotate(calc(-1 * var(--beam-br, -35deg)))",
                transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
                willChange: "transform",
                background: "radial-gradient(ellipse at center, color-mix(in srgb, var(--text-primary) 30%, transparent) 0%, rgba(165, 180, 253, 0.15) 40%, transparent 80%)",
                mixBlendMode: "screen",
            }}
        />
      </Box>

      {/* Ray 2: CORE GLOW */}
      <Box
        sx={{
          position: "absolute",
          bottom: "-150px",
          right: "calc(-50px - 17.5vw)",
          width: "35vw",
          height: "150vh",
          transformOrigin: "bottom center",
          transform: "rotate(var(--beam-br, -35deg))",
          transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
          willChange: "transform",
          opacity: 0.25,
          filter: "blur(50px)",
          zIndex: 1,
          pointerEvents: "none",
          mixBlendMode: "screen",
        }}
      >
        <Box sx={{
          width: "100%",
          height: "100%",
          background: "linear-gradient(to top, rgba(196, 181, 253, 0.45) 0%, rgba(139, 92, 246, 0.1) 90%, transparent 100%)",
          clipPath: "polygon(20% 0%, 80% 0%, 55% 100%, 45% 100%)",
        }} />
      </Box>
      </>
      )}

      {/* 5. GRAIN TEXTURE */}
      {/* 5. GRAIN TEXTURE - REMOVED for Clean Sanctuary Look */}
      {/* <Box sx={{
        position: "absolute",
        inset: 0,
        backgroundImage: GRAIN_TEXTURE,
        opacity: 0.07,
        pointerEvents: "none",
        zIndex: 3,
      }} /> */}

      {/* CONTENT WRAPPER */}
      <Box 
        sx={{ 
          position: "relative", 
          zIndex: 5, 
          pt: disablePadding ? 0 : "90px", // Standard Layout padding
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

VolumetricLayout.propTypes = {
  children: PropTypes.node.isRequired,
  enableLights: PropTypes.bool,
  variant: PropTypes.oneOf(["warm", "cool", "platinum", "void", "deep-cosmos"]),
  disablePadding: PropTypes.bool,
};

export default VolumetricLayout;
